import React from 'react'
import { css } from '@emotion/react'
import { useLinkComponent } from '../../../links/base/useHooks.jsx'

/**
 * ハンバーガーメニューボタン
 */
export const MenuButton = ({ onClick, isOpen }) => (
  <button 
    onClick={onClick}
    css={css`
      width: ${isOpen ? '20px' : '20px'};
      height: ${isOpen ? '19px' : '14px'};
      background: none;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      outline: none;
      box-shadow: none;
    `}
  >
    {isOpen ? (
      <svg width="20" height="19" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 19.1776L0 17.3421L8 10.0001L0 2.65802L2 0.82251L10 8.16455L18 0.82251L20 2.65802L12 10.0001L20 17.3421L18 19.1776L10 11.8356L2 19.1776Z" fill="#F4F5F7"/>
      </svg>
    ) : (
      <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M0 13.1183V11.0789H20V13.1183H0ZM0 8.01968V5.98023H20V8.01968H0ZM0 2.92105V0.881592H20V2.92105H0Z" fill="#F4F5F7"/>
      </svg>
    )}
  </button>
)

/**
 * メニューコンテナ
 */
export const MenuContainer = ({ children }) => (
  <div css={css`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1020;
  `}>
    {children}
  </div>
)

/**
 * オーバーレイ（メニュー外クリック用）
 */
export const Overlay = ({ isVisible, onClick }) => (
  <div 
    onClick={onClick}
    css={css`
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: transparent;
      z-index: 1005;
      display: ${isVisible ? 'block' : 'none'};
    `}
  />
)

/**
 * メニューパネル
 */
export const MenuPanel = ({ children, isOpen }) => (
  <div css={css`
    position: fixed;
    width: 372px;
    top: 60px;
    bottom: 0;
    right: 0px;
    background: rgba(28, 28, 28, 0.95);
    transform: translateX(${isOpen ? '0' : '100%'});
    transition: transform 0.3s ease;
    z-index: 1010;
    display: flex;
    flex-direction: column;
    @media (max-width: 767px) {
      width: 100vw;
      min-width: 0;
      left: 0;
      right: 0;
      top: 40px;
    }
  `}>
    {/* 内側ラッパー（100% 高さで flex） */}
    <div css={css`
      display: flex;
      flex-direction: column;
      height: 100%;
    `}>
      {children}
    </div>
  </div>
)

/**
 * 戻るアイコン
 */
export const BackIcon = () => (
  <svg 
    width="9" 
    height="16" 
    viewBox="0 0 9 16" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
    css={css`
      position: absolute;
      top: 4.5px;
    `}
  >
    <path d="M7.59378 -1.38494e-07L8.94165 1.42L2.69588 8L8.94165 14.58L7.59378 16L0.000133156 8L7.59378 -1.38494e-07Z" fill="#F4F5F7"/>
  </svg>
)

/**
 * 区切り線
 */
export const Divider = () => (
  <div css={css`
    width: 100%;
    height: 0px;
    border: 1px solid #F4F5F7;
  `} />
)

/**
 * 戻るボタン
 */
export const BackButton = ({ onClick }) => (
  <div 
    onClick={onClick} 
    css={css`
      cursor: pointer;
      width: 100%;
      height: 25px;
      position: relative;
      flex: none;
      align-self: stretch;
      flex-grow: 0;
    `}
  >
    <BackIcon />
    <div css={css`
      width: 100%;
      height: 25px;
      font-weight: 400;
      font-size: 14px;
      line-height: 25px;
      letter-spacing: 0.05em;
      color: #F4F5F7;
      padding-left: 18px;
    `}>
      戻る
    </div>
  </div>
)

/**
 * スクロール領域
 */
export const ScrollArea = ({ children }) => (
  <div css={css`
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 32px 32px;
    @media (max-width: 767px) {
      padding: 12px 28px 28px;
    }
  `}>
    {children}
  </div>
);

/**
 * サブメニューのヘッダー
 */
export const SubmenuHeader = ({ onBack }) => (
  <div css={css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 32px 0;   /* 上16 右左32 下0 */
  `}>
    <BackButton onClick={onBack} />
    <Divider />
  </div>
);

/**
 * サブメニューコンテンツ
 */
export const SubmenuContent = ({ children }) => (
  <div css={css`
    display: flex;
    flex-direction: column;
    gap: 24px;
    width: 100%;
  `}>
    {children}
  </div>
)

/**
 * メニュー項目
 */
export const MenuItem = ({ text, onClick, href, hasArrow, onLinkClick, target, rel }) => {
  const Content = () => (
    <div css={css`
      width: 100%;
      height: 25px;
      position: relative;
      flex: none;
      align-self: stretch;
      flex-grow: 0;
    `}>
      <div css={css`
        width: 100%;
        height: 25px;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 25px;
        letter-spacing: 0.05em;
        color: #F4F5F7;
      `}>
        {text}

        {hasArrow && (
          <div css={css`
            position: absolute;
            width: 11.72px;
            height: 16px;
            right: 0;
            top: 4.5px;
            background: #F4F5F7;
            mask-image: url("data:image/svg+xml,%3Csvg width='12' height='16' viewBox='0 0 12 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 14.1176L7.05882 8L0 1.88235L1.41176 0L10 8L1.41176 16L0 14.1176Z' fill='%23F4F5F7'/%3E%3C/svg%3E");
            mask-repeat: no-repeat;
            mask-position: center;
          `} />
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <a
        href={href}
        onClick={onLinkClick}
        target={target}
        rel={rel}
        css={css`
          text-decoration: none;
          color: inherit;
          display: block;
          cursor: pointer;
        `}
      >
        <Content />
      </a>
    );
  }

  return (
    <div onClick={onClick} css={css`cursor: pointer;`}>
      <Content />
    </div>
  );
}

/**
 * 加号アイコン
 */
export const AddIcon = () => (
  <svg width="12" height="11" viewBox="0 0 12 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 6.28571H6.85714V11H5.14286V6.28571H0V4.71429H5.14286V0H6.85714V4.71429H12V6.28571Z" fill="#F4F5F7"/>
  </svg>
)

/**
 * カテゴリ項目（中カテゴリ表示ボタン付き）
 */
export const CategoryItem = ({ text, href, onSubcategoryClick, onLinkClick }) => {
  const Link = useLinkComponent();

  return (
    <div css={css`
      width: 100%;
      height: 25px;
      position: relative;
      flex: none;
      align-self: stretch;
      flex-grow: 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
    `}>
      <Link
        to={href}
        onClick={onLinkClick}
        css={css`
          text-decoration: none;
          font-style: normal;
          font-weight: 400;
          font-size: 14px;
          line-height: 25px;
          letter-spacing: 0.05em;
          color: #F4F5F7;
        `}
      >
        {text}
      </Link>
      
      <div 
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onSubcategoryClick();
        }}
        css={css`
          display: flex;
          flex-direction: row;
          justify-content: center;
          align-items: center;
          padding: 0px 4px;
          gap: 4px;
          height: 20px;
          cursor: pointer;
        `}
      >
        <AddIcon />
        <span css={css`
          font-style: normal;
          font-weight: 400;
          font-size: 10px;
          line-height: 24px;
          color: #F4F5F7;
          white-space: nowrap;
        `}>
          中カテゴリを表示
        </span>
      </div>
    </div>
  );
} 
